import Titulo from '../../components/tipografia/Titulo';
import React, { useCallback, useState } from 'react';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import Icon from '../../components/Icon';
import { mdiDownload, mdiTrashCan, mdiLoading, mdiDatabaseCog, mdiClockAlert, mdiMessageText, mdiTicket } from '@mdi/js';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import CampoData from '../../components/Formulario/CampoData';
import { format, parseISO } from 'date-fns';

type ResponseList = {
  status: number;
  ok?: boolean;
  result: any[];
  total: number;
  cursor: string | number | null;
};

type Backup = {
  id: number;
  data_backup: string;
  data_inicial: string;
  status: 'pendente' | 'processando' | 'concluido' | 'erro';
  tamanho?: string;
  arquivo_path?: string;
  erro_mensagem?: string;
};

type RegistroInfo = {
  data_criacao: string;
  dias_restantes: number;
  data_exclusao: string;
};

type InfoRegistrosAntigos = {
  chat: RegistroInfo | null;
  ticket: RegistroInfo | null;
  tempo_vida_dias: number;
};

const Page = () => {
  const [dataInicial, setDataInicial] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const queryClient = useQueryClient();

  // Query para buscar informações dos registros mais antigos
  const queryInfoRegistros = useQuery(
    ['info-registros-antigos'],
    async () => {
      const result = await axios.get<InfoRegistrosAntigos>('/api/backup/info-registros-antigos');
      return result.data;
    },
    {
      refetchInterval: 60000, // Atualizar a cada minuto
    }
  );

  const headers: DataTableHeader[] = React.useMemo(
    () => [
      {
        label: 'Data do Backup',
        width: '20%',
        render: (row: Backup) =>
          format(parseISO(row.data_backup), 'dd/MM/yyyy HH:mm'),
      },
      {
        label: 'Data Inicial',
        width: '20%',
        render: (row: Backup) =>
          format(parseISO(row.data_inicial), 'dd/MM/yyyy HH:mm'),
      },
      {
        label: 'Status',
        width: '15%',
        render: (row: Backup) => {
          const statusMap: Record<string, string> = {
            pendente: 'Pendente',
            processando: 'Processando',
            concluido: 'Concluído',
            erro: 'Erro',
          };

          const statusClassMap: Record<string, string> = {
            concluido: styles.statusConcluido,
            pendente: styles.statusProcessando,
            processando: styles.statusProcessando,
            erro: styles.statusErro,
          };

          return (
            <span className={`${styles.statusBadge} ${statusClassMap[row.status]}`}>
              {statusMap[row.status] || row.status}
            </span>
          );
        },
      },
      {
        label: 'Tamanho',
        width: '15%',
        render: (row: Backup) => row.tamanho || '-',
      },
      {
        label: 'Ações',
        width: '120px',
        stickRight: true,
        alignRight: true,
        render: (row: Backup) => {
          return (
            <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
              {row.status === 'concluido' && row.arquivo_path && (
                <Botao
                  title="Download"
                  variant="none-primary"
                  icon={mdiDownload}
                  onClick={() => handleDownload(row)}
                />
              )}
              <BotaoPerigoso
                variant="none-danger"
                icon={mdiTrashCan}
                onClick={() => handleDelete(row.id)}
                buttonProps={{
                  title: 'Excluir',
                }}
                confirmation="Deseja realmente excluir este backup?"
              />
            </HBox>
          );
        },
      },
    ],
    []
  );

  const queryList = useInfiniteQuery(
    ['backup-historico'],
    async ({ pageParam = null }) => {
      const params: Record<string, string> = {};
      if (pageParam) params.cursor = pageParam;

      const result = await axios.get('/api/backup', { params });
      return result;
    },
    {
      getNextPageParam: (lastPage: any) => {
        if (!lastPage?.data?.cursor) return undefined;
        return lastPage?.data?.cursor;
      },
      refetchInterval: 5000, // Atualizar a cada 5 segundos para ver status
    }
  );

  const queryFlatList = queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
  const total = queryList.data?.pages?.[0]?.data?.total ?? 0;

  const mutationCreate = useMutation(
    async () => {
      if (!dataInicial) {
        throw new Error('Data inicial é obrigatória');
      }

      const dataInicialDate = new Date(dataInicial);
      const hoje = new Date();

      if (dataInicialDate > hoje) {
        throw new Error('Data inicial não pode ser futura');
      }

      const result = await axios.post(
        '/api/backup',
        { data_inicial: dataInicial },
        { withCredentials: true }
      );
      return result;
    },
    {
      onSuccess: () => {
        toast('Backup iniciado com sucesso', {
          toastId: 'backup-success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
        setShowConfirmModal(false);
        setDataInicial('');
        queryClient.invalidateQueries(['backup-historico']);
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || error.message || 'Erro ao iniciar backup';
        toast(message, {
          toastId: 'backup-error',
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
      },
    }
  );

  const mutationDelete = useMutation(
    async (id: number) => {
      const result = await axios.delete(`/api/backup/${id}`, {
        withCredentials: true,
      });
      return result;
    },
    {
      onSuccess: () => {
        toast('Backup excluído com sucesso', {
          toastId: 'backup-delete-success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
        queryClient.invalidateQueries(['backup-historico']);
      },
      onError: (error: any) => {
        toast('Erro ao excluir backup', {
          toastId: 'backup-delete-error',
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
      },
    }
  );

  const handleDelete = useCallback(
    (id: number) => {
      mutationDelete.mutate(id);
    },
    [mutationDelete]
  );

  const handleDownload = useCallback(async (backup: Backup) => {
    try {
      const response = await axios.get(`/api/backup/${backup.id}/download`, {
        responseType: 'blob',
        withCredentials: true,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `backup_${format(parseISO(backup.data_backup), 'yyyyMMdd_HHmmss')}.zip`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast('Erro ao fazer download do backup', {
        toastId: 'backup-download-error',
        position: 'top-center',
        type: 'error',
        autoClose: 5000,
      });
    }
  }, []);

  const handleGerarBackup = () => {
    if (!dataInicial) {
      toast('Data inicial é obrigatória', {
        toastId: 'data-obrigatoria',
        position: 'top-center',
        type: 'error',
        autoClose: 5000,
      });
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmarBackup = () => {
    mutationCreate.mutate();
  };

  return (
    <LayoutUsuario>
      <div className={styles.container}>
        <Titulo>Gerenciamento de Dados</Titulo>
        <Descricao>
          Gerencie backups de conversas e tickets do sistema
        </Descricao>

        {/* Seção de Informações de Registros Antigos */}
        {!queryInfoRegistros.isLoading && (queryInfoRegistros.data?.chat || queryInfoRegistros.data?.ticket) && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <span>
                <Icon path={mdiClockAlert} size={1} style={{ marginRight: 8 }} />
              </span>
              Registros Mais Antigos
            </h2>

            <div className={styles.registrosAntigos}>
              {queryInfoRegistros.data?.chat && (
                <div className={styles.registroCard}>
                  <div className={styles.registroHeader}>
                    <Icon path={mdiMessageText} size={1} />
                    <h3>Chat</h3>
                  </div>
                  <div className={styles.registroInfo}>
                    <div className={styles.registroItem}>
                      <span className={styles.registroLabel}>Registro mais antigo:</span>
                      <span className={styles.registroValue}>
                        {format(parseISO(queryInfoRegistros.data.chat.data_criacao), 'dd/MM/yyyy HH:mm')}
                      </span>
                    </div>
                    <div className={styles.registroItem}>
                      <span className={styles.registroLabel}>Data de exclusão:</span>
                      <span className={styles.registroValue}>
                        {format(parseISO(queryInfoRegistros.data.chat.data_exclusao), 'dd/MM/yyyy')}
                      </span>
                    </div>
                    <div className={styles.registroItem}>
                      <span className={styles.registroLabel}>Tempo restante:</span>
                      <span className={`${styles.registroValue} ${queryInfoRegistros.data.chat.dias_restantes <= 30 ? styles.diasCritico : styles.diasNormal}`}>
                        {queryInfoRegistros.data.chat.dias_restantes} {queryInfoRegistros.data.chat.dias_restantes === 1 ? 'dia' : 'dias'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {queryInfoRegistros.data?.ticket && (
                <div className={styles.registroCard}>
                  <div className={styles.registroHeader}>
                    <Icon path={mdiTicket} size={1} />
                    <h3>Ticket</h3>
                  </div>
                  <div className={styles.registroInfo}>
                    <div className={styles.registroItem}>
                      <span className={styles.registroLabel}>Registro mais antigo:</span>
                      <span className={styles.registroValue}>
                        {format(parseISO(queryInfoRegistros.data.ticket.data_criacao), 'dd/MM/yyyy HH:mm')}
                      </span>
                    </div>
                    <div className={styles.registroItem}>
                      <span className={styles.registroLabel}>Data de exclusão:</span>
                      <span className={styles.registroValue}>
                        {format(parseISO(queryInfoRegistros.data.ticket.data_exclusao), 'dd/MM/yyyy')}
                      </span>
                    </div>
                    <div className={styles.registroItem}>
                      <span className={styles.registroLabel}>Tempo restante:</span>
                      <span className={`${styles.registroValue} ${queryInfoRegistros.data.ticket.dias_restantes <= 30 ? styles.diasCritico : styles.diasNormal}`}>
                        {queryInfoRegistros.data.ticket.dias_restantes} {queryInfoRegistros.data.ticket.dias_restantes === 1 ? 'dia' : 'dias'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.infoBox} style={{ marginTop: 16 }}>
              <p style={{ margin: 0 }}>
                <strong>⚠️ Atenção:</strong> Os registros são automaticamente excluídos após {queryInfoRegistros.data?.tempo_vida_dias || 180} dias.
                Faça backup dos dados importantes antes da data de exclusão.
              </p>
            </div>
          </div>
        )}

        {/* Seção de Geração de Backup */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Backup de Conversas e Tickets</h2>

          <div className={styles.infoBox}>
            <p>
              <strong>Informações sobre o backup:</strong>
            </p>
            <ul style={{ marginTop: 8, marginLeft: 20 }}>
              <li>O backup incluirá todas as conversas e tickets criados ANTES da data inicial selecionada (registros mais antigos)</li>
              <li>Conversas e tickets são <b style={{ color: 'var(--tc-color-danger)' }}>excluídos permanentemente após 180 dias</b></li>
              <li>O processamento pode levar alguns minutos dependendo do volume de dados</li>
              <li>Os registros incluídos no backup serão marcados e não aparecerão em futuros backups</li>
              <li>Se um backup for excluído, os registros poderão ser incluídos novamente em um novo backup</li>
              <li>Os backups são <b style={{ color: 'var(--tc-color-danger)' }}>excluídos permanentemente após 30 dias</b></li>
              <li>Os backups são armazenados em formato compactado (.zip)</li>
            </ul>
          </div>

          <div className={styles.formGrid}>
            <CampoData
              label="Data Inicial"
              type="datetime-local"
              value={dataInicial}
              onChange={setDataInicial}
              instanceId="data_inicial"
              required
            />
            <Botao
              variant="primary"
              onClick={handleGerarBackup}
              disabled={mutationCreate.isLoading}
            >
              {mutationCreate.isLoading ? (
                <>
                  <Icon path={mdiLoading} spin size={1} />
                  Gerando...
                </>
              ) : (
                'Gerar Backup'
              )}
            </Botao>
          </div>
        </div>

        {/* Seção de Histórico */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Histórico de Backups</h2>

          <div className={styles.tableContainer}>
            <DataTable
              cabecalhos={headers}
              linhas={queryFlatList}
              isLoading={queryList.isLoading}
              hasMoreData={queryList.hasNextPage}
              onLoadMore={() => queryList.fetchNextPage()}
            />
          </div>

          {!queryList.isLoading && queryFlatList.length === 0 && (
            <div className={styles.emptyState}>
              <Icon path={mdiDatabaseCog} size={3} />
              <p>Nenhum backup encontrado</p>
              <p style={{ fontSize: '0.9rem', marginTop: 8 }}>
                Clique em "Gerar Backup" para criar seu primeiro backup
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Confirmação */}
      <Modal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmarBackup}
        title="Confirmar Geração de Backup"
      >
        <VBox gap="16px">
          <p>
            Deseja realmente gerar o backup de conversas e tickets a partir de{' '}
            <strong>
              {dataInicial && format(new Date(dataInicial), 'dd/MM/yyyy HH:mm')}
            </strong>
            ?
          </p>
          <p style={{ fontSize: '0.9rem', color: 'var(--tc-color-gray-600)' }}>
            Este processo pode levar alguns minutos. Você poderá acompanhar o
            progresso na tabela de histórico.
          </p>
        </VBox>
      </Modal>
    </LayoutUsuario>
  );
};

export default Page;
